/**
 * Vendored from react-avatar-editor v13.0.2 (MIT).
 * https://github.com/mosch/react-avatar-editor
 */
import PropTypes from 'prop-types';
import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react';

import loadImageURL from './utils/load-image-url';
import loadImageFile from './utils/load-image-file';

const isTouchDevice = !!(
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    ('ontouchstart' in window || navigator.msMaxTouchPoints > 0)
);

const isFileAPISupported = typeof File !== 'undefined';

const isPassiveSupported = () => {
    // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
    let passiveSupported = false;
    try {
        const options = Object.defineProperty({}, 'passive', {
            get: function () {
                passiveSupported = true;
                return undefined;
            },
        });

        window.addEventListener('test', options, options);
        window.removeEventListener('test', options, options);
    } catch (err) {
        passiveSupported = false;
    }
    return passiveSupported;
};

const draggableEvents = {
    touch: {
        react: {
            down: 'onTouchStart',
            mouseDown: 'onMouseDown',
            drag: 'onTouchMove',
            move: 'onTouchMove',
            mouseMove: 'onMouseMove',
            up: 'onTouchEnd',
            mouseUp: 'onMouseUp',
        },
        native: {
            down: 'touchstart',
            mouseDown: 'mousedown',
            drag: 'touchmove',
            move: 'touchmove',
            mouseMove: 'mousemove',
            up: 'touchend',
            mouseUp: 'mouseup',
        },
    },
    desktop: {
        react: {
            down: 'onMouseDown',
            drag: 'onDragOver',
            move: 'onMouseMove',
            up: 'onMouseUp',
        },
        native: {
            down: 'mousedown',
            drag: 'dragStart',
            move: 'mousemove',
            up: 'mouseup',
        },
    },
};
const deviceEvents = isTouchDevice ? draggableEvents.touch : draggableEvents.desktop;

let pixelRatio =
    typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;

// Draws a rounded rectangle on a 2D context.
const drawRoundedRect = (context, x, y, width, height, borderRadius) => {
    if (borderRadius === 0) {
        context.rect(x, y, width, height);
    } else {
        const widthMinusRad = width - borderRadius;
        const heightMinusRad = height - borderRadius;
        context.translate(x, y);
        context.arc(borderRadius, borderRadius, borderRadius, Math.PI, Math.PI * 1.5);
        context.lineTo(widthMinusRad, 0);
        context.arc(widthMinusRad, borderRadius, borderRadius, Math.PI * 1.5, Math.PI * 2);
        context.lineTo(width, heightMinusRad);
        context.arc(widthMinusRad, heightMinusRad, borderRadius, Math.PI * 2, Math.PI * 0.5);
        context.lineTo(borderRadius, height);
        context.arc(borderRadius, heightMinusRad, borderRadius, Math.PI * 0.5, Math.PI);
        context.translate(-x, -y);
    }
};

const defaultEmptyImage = {
    x: 0.5,
    y: 0.5,
};

const AvatarEditor = forwardRef(function AvatarEditor(
    {
        scale = 1,
        rotate = 0,
        image: imageProp,
        border = 25,
        borderRadius = 0,
        width = 200,
        height = 200,
        position,
        color = [0, 0, 0, 0.5],
        backgroundColor,
        crossOrigin,
        onLoadFailure = () => {},
        onLoadSuccess = () => {},
        onImageReady = () => {},
        onImageChange = () => {},
        onMouseUp = () => {},
        onMouseMove = () => {},
        onPositionChange = () => {},
        disableBoundaryChecks = false,
        disableHiDPIScaling = false,
        disableCanvasRotation = true,
        style,
        ...rest
    },
    ref,
) {
    const canvasRef = useRef(null);
    const loadingImageRef = useRef(null);

    const [drag, setDrag] = useState(false);
    const [mx, setMx] = useState(null);
    const [my, setMy] = useState(null);
    const [image, setImage] = useState(defaultEmptyImage);

    const propsRef = useRef({
        scale,
        rotate,
        image: imageProp,
        border,
        borderRadius,
        width,
        height,
        position,
        color,
        backgroundColor,
        crossOrigin,
        onLoadFailure,
        onLoadSuccess,
        onImageReady,
        onImageChange,
        onMouseUp,
        onMouseMove,
        onPositionChange,
        disableBoundaryChecks,
        disableHiDPIScaling,
        disableCanvasRotation,
    });
    propsRef.current = {
        scale,
        rotate,
        image: imageProp,
        border,
        borderRadius,
        width,
        height,
        position,
        color,
        backgroundColor,
        crossOrigin,
        onLoadFailure,
        onLoadSuccess,
        onImageReady,
        onImageChange,
        onMouseUp,
        onMouseMove,
        onPositionChange,
        disableBoundaryChecks,
        disableHiDPIScaling,
        disableCanvasRotation,
    };

    const stateRef = useRef({ drag, mx, my, image });
    stateRef.current = { drag, mx, my, image };

    const isVertical = useCallback(() => {
        const { disableCanvasRotation: disableRotation, rotate: rotation } = propsRef.current;
        return !disableRotation && rotation % 180 !== 0;
    }, []);

    const getBorders = useCallback((borderValue = propsRef.current.border) => {
        return Array.isArray(borderValue) ? borderValue : [borderValue, borderValue];
    }, []);

    const getDimensions = useCallback(() => {
        const {
            width: canvasWidthProp,
            height: canvasHeightProp,
            rotate: rotation,
            border: borderProp,
        } = propsRef.current;

        const canvas = {};
        const [borderX, borderY] = getBorders(borderProp);

        const canvasWidth = canvasWidthProp;
        const canvasHeight = canvasHeightProp;

        if (isVertical()) {
            canvas.width = canvasHeight;
            canvas.height = canvasWidth;
        } else {
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
        }

        canvas.width += borderX * 2;
        canvas.height += borderY * 2;

        return {
            canvas,
            rotate: rotation,
            width: canvasWidthProp,
            height: canvasHeightProp,
            border: borderProp,
        };
    }, [getBorders, isVertical]);

    const getXScale = useCallback(() => {
        const { width: canvasWidth, height: canvasHeight } = propsRef.current;
        const { image: imageState } = stateRef.current;
        const canvasAspect = canvasWidth / canvasHeight;
        const imageAspect = imageState.width / imageState.height;

        return Math.min(1, canvasAspect / imageAspect);
    }, []);

    const getYScale = useCallback(() => {
        const { width: canvasWidth, height: canvasHeight } = propsRef.current;
        const { image: imageState } = stateRef.current;
        const canvasAspect = canvasHeight / canvasWidth;
        const imageAspect = imageState.height / imageState.width;

        return Math.min(1, canvasAspect / imageAspect);
    }, []);

    const getCroppingRect = useCallback(() => {
        const {
            scale: scaleProp,
            position: positionProp,
            disableBoundaryChecks: disableChecks,
        } = propsRef.current;
        const { image: imageState } = stateRef.current;

        const imagePosition = positionProp || {
            x: imageState.x,
            y: imageState.y,
        };
        const cropWidth = (1 / scaleProp) * getXScale();
        const cropHeight = (1 / scaleProp) * getYScale();

        const croppingRect = {
            x: imagePosition.x - cropWidth / 2,
            y: imagePosition.y - cropHeight / 2,
            width: cropWidth,
            height: cropHeight,
        };

        let xMin = 0;
        let xMax = 1 - croppingRect.width;
        let yMin = 0;
        let yMax = 1 - croppingRect.height;

        const isLargerThanImage = disableChecks || cropWidth > 1 || cropHeight > 1;

        if (isLargerThanImage) {
            xMin = -croppingRect.width;
            xMax = 1;
            yMin = -croppingRect.height;
            yMax = 1;
        }

        return {
            ...croppingRect,
            x: Math.max(xMin, Math.min(croppingRect.x, xMax)),
            y: Math.max(yMin, Math.min(croppingRect.y, yMax)),
        };
    }, [getXScale, getYScale]);

    const getInitialSize = useCallback(
        (imageWidth, imageHeight) => {
            let newHeight;
            let newWidth;

            const dimensions = getDimensions();
            const canvasRatio = dimensions.height / dimensions.width;
            const imageRatio = imageHeight / imageWidth;

            if (canvasRatio > imageRatio) {
                newHeight = getDimensions().height;
                newWidth = imageWidth * (newHeight / imageHeight);
            } else {
                newWidth = getDimensions().width;
                newHeight = imageHeight * (newWidth / imageWidth);
            }

            return {
                height: newHeight,
                width: newWidth,
            };
        },
        [getDimensions],
    );

    const calculatePosition = useCallback(
        (imageState, borderValue) => {
            const imageData = imageState || stateRef.current.image;
            const { scale: scaleProp } = propsRef.current;

            const [borderX, borderY] = getBorders(borderValue);
            const croppingRect = getCroppingRect();

            const imageWidth = imageData.width * scaleProp;
            const imageHeight = imageData.height * scaleProp;

            let x = -croppingRect.x * imageWidth;
            let y = -croppingRect.y * imageHeight;

            if (isVertical()) {
                x += borderY;
                y += borderX;
            } else {
                x += borderX;
                y += borderY;
            }

            return {
                x,
                y,
                height: imageHeight,
                width: imageWidth,
            };
        },
        [getBorders, getCroppingRect, isVertical],
    );

    const paintImage = useCallback(
        (context, imageState, borderValue, scaleFactor = pixelRatio) => {
            if (!imageState.resource) {
                return;
            }

            const { rotate: rotation } = propsRef.current;
            const position = calculatePosition(imageState, borderValue);

            context.save();

            context.translate(context.canvas.width / 2, context.canvas.height / 2);
            context.rotate((rotation * Math.PI) / 180);
            context.translate(-(context.canvas.width / 2), -(context.canvas.height / 2));

            if (isVertical()) {
                context.translate(
                    (context.canvas.width - context.canvas.height) / 2,
                    (context.canvas.height - context.canvas.width) / 2,
                );
            }

            context.scale(scaleFactor, scaleFactor);

            context.globalCompositeOperation = 'destination-over';
            context.drawImage(
                imageState.resource,
                position.x,
                position.y,
                position.width,
                position.height,
            );

            if (imageState.backgroundColor) {
                context.fillStyle = imageState.backgroundColor;
                context.fillRect(position.x, position.y, position.width, position.height);
            }

            context.restore();
        },
        [calculatePosition, isVertical],
    );

    const paint = useCallback(
        (context) => {
            const { color: colorProp, borderRadius: borderRadiusProp } = propsRef.current;

            context.save();
            context.scale(pixelRatio, pixelRatio);
            context.translate(0, 0);
            context.fillStyle = 'rgba(' + colorProp.slice(0, 4).join(',') + ')';

            let resolvedBorderRadius = borderRadiusProp;
            const dimensions = getDimensions();
            const [borderSizeX, borderSizeY] = getBorders(dimensions.border);
            const canvasHeight = dimensions.canvas.height;
            const canvasWidth = dimensions.canvas.width;

            resolvedBorderRadius = Math.max(resolvedBorderRadius, 0);
            resolvedBorderRadius = Math.min(
                resolvedBorderRadius,
                canvasWidth / 2 - borderSizeX,
                canvasHeight / 2 - borderSizeY,
            );

            context.beginPath();
            drawRoundedRect(
                context,
                borderSizeX,
                borderSizeY,
                canvasWidth - borderSizeX * 2,
                canvasHeight - borderSizeY * 2,
                resolvedBorderRadius,
            );
            context.rect(canvasWidth, 0, -canvasWidth, canvasHeight);
            context.fill('evenodd');

            context.restore();
        },
        [getBorders, getDimensions],
    );

    const handleImageReady = useCallback(
        (loadedImage) => {
            const {
                backgroundColor: bgColor,
                onImageReady: onReady,
                onLoadSuccess: onSuccess,
            } = propsRef.current;
            const imageState = getInitialSize(loadedImage.width, loadedImage.height);
            imageState.resource = loadedImage;
            imageState.x = 0.5;
            imageState.y = 0.5;
            imageState.backgroundColor = bgColor;
            setDrag(false);
            setImage(imageState);
            onReady();
            onSuccess(imageState);
        },
        [getInitialSize],
    );

    const loadImage = useCallback(
        (imageSource) => {
            const { crossOrigin: crossOriginProp, onLoadFailure: onFailure } = propsRef.current;

            if (isFileAPISupported && imageSource instanceof File) {
                loadingImageRef.current = loadImageFile(imageSource)
                    .then(handleImageReady)
                    .catch(onFailure);
            } else if (typeof imageSource === 'string') {
                loadingImageRef.current = loadImageURL(imageSource, crossOriginProp)
                    .then(handleImageReady)
                    .catch(onFailure);
            }
        },
        [handleImageReady],
    );

    const clearImage = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const context = canvas.getContext('2d');
            context.clearRect(0, 0, canvas.width, canvas.height);
        }
        setImage(defaultEmptyImage);
    }, []);

    const getImage = useCallback(() => {
        const { rotate: rotation } = propsRef.current;
        const { image: imageState } = stateRef.current;
        const cropRect = getCroppingRect();

        cropRect.x *= imageState.resource.width;
        cropRect.y *= imageState.resource.height;
        cropRect.width *= imageState.resource.width;
        cropRect.height *= imageState.resource.height;

        const canvas = document.createElement('canvas');

        if (isVertical()) {
            canvas.width = cropRect.height;
            canvas.height = cropRect.width;
        } else {
            canvas.width = cropRect.width;
            canvas.height = cropRect.height;
        }

        const context = canvas.getContext('2d');

        context.translate(canvas.width / 2, canvas.height / 2);
        context.rotate((rotation * Math.PI) / 180);
        context.translate(-(canvas.width / 2), -(canvas.height / 2));

        if (isVertical()) {
            context.translate(
                (canvas.width - canvas.height) / 2,
                (canvas.height - canvas.width) / 2,
            );
        }

        if (imageState.backgroundColor) {
            context.fillStyle = imageState.backgroundColor;
            context.fillRect(
                -cropRect.x,
                -cropRect.y,
                imageState.resource.width,
                imageState.resource.height,
            );
        }

        context.drawImage(imageState.resource, -cropRect.x, -cropRect.y);

        return canvas;
    }, [getCroppingRect, isVertical]);

    const getImageScaledToCanvas = useCallback(() => {
        const { image: imageState } = stateRef.current;
        const { width: canvasWidth, height: canvasHeight } = getDimensions();
        const canvas = document.createElement('canvas');

        if (isVertical()) {
            canvas.width = canvasHeight;
            canvas.height = canvasWidth;
        } else {
            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
        }

        paintImage(canvas.getContext('2d'), imageState, 0, 1);

        return canvas;
    }, [getDimensions, isVertical, paintImage]);

    useImperativeHandle(
        ref,
        () => ({
            getImage,
            getImageScaledToCanvas,
        }),
        [getImage, getImageScaledToCanvas],
    );

    const handleMouseDown = useCallback((e) => {
        e = e || window.event;
        e.preventDefault();
        setDrag(true);
        setMx(null);
        setMy(null);
    }, []);

    const handleMouseUp = useCallback(() => {
        const { drag: isDragging } = stateRef.current;
        const { onMouseUp: onUp } = propsRef.current;

        if (isDragging) {
            setDrag(false);
            onUp();
        }
    }, []);

    const handleMouseMove = useCallback(
        (e) => {
            e = e || window.event;
            const {
                drag: isDragging,
                mx: prevMx,
                my: prevMy,
                image: imageState,
            } = stateRef.current;
            const {
                rotate: rotation,
                scale: scaleProp,
                onPositionChange: onPosition,
                onMouseMove: onMove,
            } = propsRef.current;

            if (isDragging === false) {
                return;
            }

            e.preventDefault();

            const mousePositionX = e.targetTouches ? e.targetTouches[0].pageX : e.clientX;
            const mousePositionY = e.targetTouches ? e.targetTouches[0].pageY : e.clientY;

            const newState = {
                mx: mousePositionX,
                my: mousePositionY,
            };

            let normalizedRotate = rotation;

            normalizedRotate %= 360;
            normalizedRotate = normalizedRotate < 0 ? normalizedRotate + 360 : normalizedRotate;

            if (prevMx && prevMy) {
                const deltaX = prevMx - mousePositionX;
                const deltaY = prevMy - mousePositionY;

                const imageWidth = imageState.width * scaleProp;
                const imageHeight = imageState.height * scaleProp;

                let { x: lastX, y: lastY } = getCroppingRect();

                lastX *= imageWidth;
                lastY *= imageHeight;

                const toRadians = (degree) => degree * (Math.PI / 180);
                const cos = Math.cos(toRadians(normalizedRotate));
                const sin = Math.sin(toRadians(normalizedRotate));

                const x = lastX + deltaX * cos + deltaY * sin;
                const y = lastY + -deltaX * sin + deltaY * cos;

                const relativeWidth = (1 / scaleProp) * getXScale();
                const relativeHeight = (1 / scaleProp) * getYScale();

                const imagePosition = {
                    x: x / imageWidth + relativeWidth / 2,
                    y: y / imageHeight + relativeHeight / 2,
                };

                onPosition(imagePosition);

                newState.image = {
                    ...imageState,
                    ...imagePosition,
                };
            }

            setMx(newState.mx);
            setMy(newState.my);
            if (newState.image) {
                setImage(newState.image);
            }

            onMove(e);
        },
        [getCroppingRect, getXScale, getYScale],
    );

    const isMountedRef = useRef(false);

    useEffect(() => {
        if (disableHiDPIScaling) {
            pixelRatio = 1;
        }

        const canvas = canvasRef.current;
        if (canvas) {
            const context = canvas.getContext('2d');
            if (imageProp) {
                loadImage(imageProp);
            }
            paint(context);
        }

        if (document) {
            const passiveSupported = isPassiveSupported();
            const thirdArgument = passiveSupported ? { passive: false } : false;
            const nativeEvents = deviceEvents.native;

            document.addEventListener(nativeEvents.move, handleMouseMove, thirdArgument);
            document.addEventListener(nativeEvents.up, handleMouseUp, thirdArgument);
            if (isTouchDevice) {
                document.addEventListener(nativeEvents.mouseMove, handleMouseMove, thirdArgument);
                document.addEventListener(nativeEvents.mouseUp, handleMouseUp, thirdArgument);
            }
        }

        isMountedRef.current = true;

        return () => {
            if (document) {
                const nativeEvents = deviceEvents.native;
                document.removeEventListener(nativeEvents.move, handleMouseMove, false);
                document.removeEventListener(nativeEvents.up, handleMouseUp, false);
                if (isTouchDevice) {
                    document.removeEventListener(nativeEvents.mouseMove, handleMouseMove, false);
                    document.removeEventListener(nativeEvents.mouseUp, handleMouseUp, false);
                }
            }
        };
    }, [disableHiDPIScaling, handleMouseMove, handleMouseUp, imageProp, loadImage, paint]);

    const prevImagePropRef = useRef(imageProp);
    const prevWidthRef = useRef(width);
    const prevHeightRef = useRef(height);
    const prevBackgroundColorRef = useRef(backgroundColor);
    const prevImageStateRef = useRef(image);

    useEffect(() => {
        if (
            (imageProp && imageProp !== prevImagePropRef.current) ||
            width !== prevWidthRef.current ||
            height !== prevHeightRef.current ||
            backgroundColor !== prevBackgroundColorRef.current
        ) {
            loadImage(imageProp);
        } else if (!imageProp && prevImageStateRef.current !== defaultEmptyImage) {
            clearImage();
        }

        prevImagePropRef.current = imageProp;
        prevWidthRef.current = width;
        prevHeightRef.current = height;
        prevBackgroundColorRef.current = backgroundColor;
    }, [backgroundColor, clearImage, height, imageProp, loadImage, width]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        paint(context);
        paintImage(context, image, border);
    }, [
        border,
        color,
        borderRadius,
        drag,
        height,
        image,
        mx,
        my,
        paint,
        paintImage,
        rotate,
        scale,
        width,
    ]);

    const prevOnImageChangeRef = useRef({
        image: imageProp,
        width,
        height,
        position,
        scale,
        rotate,
        mx,
        my,
        imageX: image.x,
        imageY: image.y,
        backgroundColor,
    });

    useEffect(() => {
        if (!isMountedRef.current) {
            prevOnImageChangeRef.current = {
                image: imageProp,
                width,
                height,
                position,
                scale,
                rotate,
                mx,
                my,
                imageX: image.x,
                imageY: image.y,
                backgroundColor,
            };
            return;
        }

        const prev = prevOnImageChangeRef.current;

        if (
            prev.image !== imageProp ||
            prev.width !== width ||
            prev.height !== height ||
            prev.position !== position ||
            prev.scale !== scale ||
            prev.rotate !== rotate ||
            prev.mx !== mx ||
            prev.my !== my ||
            prev.imageX !== image.x ||
            prev.imageY !== image.y ||
            prev.backgroundColor !== backgroundColor
        ) {
            onImageChange();
        }

        prevOnImageChangeRef.current = {
            image: imageProp,
            width,
            height,
            position,
            scale,
            rotate,
            mx,
            my,
            imageX: image.x,
            imageY: image.y,
            backgroundColor,
        };
        prevImageStateRef.current = image;
    }, [
        backgroundColor,
        height,
        image,
        imageProp,
        mx,
        my,
        onImageChange,
        position,
        rotate,
        scale,
        width,
    ]);

    const dimensions = getDimensions();
    const defaultStyle = {
        width: dimensions.canvas.width,
        height: dimensions.canvas.height,
        cursor: drag ? 'grabbing' : 'grab',
        touchAction: 'none',
    };

    const attributes = {
        width: dimensions.canvas.width * pixelRatio,
        height: dimensions.canvas.height * pixelRatio,
        style: {
            ...defaultStyle,
            ...style,
        },
    };

    attributes[deviceEvents.react.down] = handleMouseDown;
    if (isTouchDevice) {
        attributes[deviceEvents.react.mouseDown] = handleMouseDown;
    }

    return <canvas ref={canvasRef} {...attributes} {...rest} />;
});

AvatarEditor.propTypes = {
    scale: PropTypes.number,
    rotate: PropTypes.number,
    image: PropTypes.oneOfType([
        PropTypes.string,
        ...(isFileAPISupported ? [PropTypes.instanceOf(File)] : []),
    ]),
    border: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
    borderRadius: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    position: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
    }),
    color: PropTypes.arrayOf(PropTypes.number),
    backgroundColor: PropTypes.string,
    crossOrigin: PropTypes.oneOf(['', 'anonymous', 'use-credentials']),
    onLoadFailure: PropTypes.func,
    onLoadSuccess: PropTypes.func,
    onImageReady: PropTypes.func,
    onImageChange: PropTypes.func,
    onMouseUp: PropTypes.func,
    onMouseMove: PropTypes.func,
    onPositionChange: PropTypes.func,
    disableBoundaryChecks: PropTypes.bool,
    disableHiDPIScaling: PropTypes.bool,
    disableCanvasRotation: PropTypes.bool,
};

export default AvatarEditor;
